import { apiFetch } from "./client";
import type { AnswerResult, ProvaArea, QuizOption, QuizQuestion } from "./progress";

/** OpenQuiz::BALLOON_SIZE: the quiz the balão opens. */
export const BALLOON_QUIZ_SIZE = 2;

/** The balão's flow: the simulado serves only questions from past ENEM provas, against a clock; enem serves a resumo instead of questions. */
export type TopicQuizIntent = "praticar" | "simulado" | "enem";

export interface TopicQuizQuestion extends QuizQuestion {
  /** The prova it came from ("ENEM 2022"); simulado only */
  exam: string | null;
}

/** What GET /api/lume/topic-quiz/ returns — OpenQuiz::quiz(). */
export interface TopicQuiz {
  topic: {
    id: number;
    name: string;
    subject: string;
    subjectId: number;
    area: ProvaArea;
    yearId: number;
    yearName: string;
  };
  questions: TopicQuizQuestion[];
}

/** What GET /api/lume/topic-quiz/?artigo=postId returns — OpenQuiz::resumo(). */
export interface TopicResumo {
  topic: TopicQuizOutcome["topic"];
  resumo: { title: string; points: { title: string; text: string }[] };
  /** Steps in the Revisão final the resumo leads to */
  revisaoTotal: number;
}

/** The student's caderno that covers the tópico, as its route needs it. */
export interface TopicQuizCaderno {
  id: number;
  subjectId: number;
}

export type TopicQuizAnswer = Omit<AnswerResult, "attemptFinished">;

/** A finished sitting kept in the tab until a caderno covering the tópico takes it. */
export interface StoredTopicQuiz {
  termId: number;
  /** The article whose balão opened the quiz: only that sitting opens its tópico in the trilha. */
  postId?: number;
  /** BALLOON_QUIZ_SIZE when the sitting was served short: save() checks it against that size. */
  size?: number;
  answers: { questionId: number; answerId: number; isCorrect: boolean }[];
}

export interface TopicQuizReview {
  questionId: number;
  answerId: number;
  isCorrect: boolean;
  correctAnswerId: number;
  explanation: string | null;
}

/** What GET /api/lume/topic-quiz/?questionId&answerId returns — OpenQuiz::answered(). */
export interface AnsweredQuestion {
  content: string | null;
  options: QuizOption[];
  correctAnswerId: number;
}

/** A finished sitting as the Tutor shows it after the login: the questions, in order, and how each went. */
export interface TopicQuizOutcome {
  intent: TopicQuizIntent;
  /** id is absent for the ENEM resumo: it is keyed by article, not by tópico */
  topic: { id?: number; name: string; subject: string; area: ProvaArea; yearId: number; yearName: string };
  /** Where "Voltar ao artigo" goes */
  article: string;
  questions: QuizQuestion[];
  reviews: TopicQuizReview[];
}

const STORAGE_KEY = "lume.topicQuiz";
const OUTCOME_KEY = "lume.topicQuizOutcome";

export function storedTopicQuizOutcome(): TopicQuizOutcome | null {
  try {
    const raw = sessionStorage.getItem(OUTCOME_KEY);
    return raw ? (JSON.parse(raw) as TopicQuizOutcome) : null;
  } catch {
    return null;
  }
}

export function storeTopicQuizOutcome(outcome: TopicQuizOutcome): void {
  try {
    sessionStorage.setItem(OUTCOME_KEY, JSON.stringify(outcome));
  } catch {
    // Storage blocked: the Tutor opens as usual, without the result.
  }
}

export function storedTopicQuiz(): StoredTopicQuiz | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredTopicQuiz) : null;
  } catch {
    return null;
  }
}

export function storeTopicQuiz(quiz: StoredTopicQuiz | null): void {
  try {
    if (quiz) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(quiz));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked: the sitting is simply not kept.
  }
}

export function fetchTopicQuiz(
  termId: number,
  q1?: number,
  size?: number,
  intent: TopicQuizIntent = "praticar"
): Promise<TopicQuiz> {
  const params = new URLSearchParams({ termId: String(termId) });
  if (q1) params.set("q1", String(q1));
  if (size) params.set("size", String(size));
  if (intent === "simulado") params.set("intencao", intent);
  return apiFetch<TopicQuiz>(`/api/lume/topic-quiz/?${params}`);
}

export function fetchTopicResumo(postId: number): Promise<TopicResumo> {
  return apiFetch<TopicResumo>(`/api/lume/topic-quiz/?artigo=${postId}`);
}

function post<T>(body: Record<string, unknown>): Promise<T> {
  return apiFetch<T>("/api/lume/topic-quiz/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function answerTopicQuiz(args: { termId: number; questionId: number; answerId: number }): Promise<TopicQuizAnswer> {
  return post<TopicQuizAnswer>({ action: "answer", ...args });
}

export function fetchAnsweredQuestion(questionId: number, answerId: number): Promise<AnsweredQuestion> {
  const params = new URLSearchParams({ questionId: String(questionId), answerId: String(answerId) });
  return apiFetch<AnsweredQuestion>(`/api/lume/topic-quiz/?${params}`);
}

export function saveTopicQuiz(quiz: StoredTopicQuiz): Promise<{ caderno: TopicQuizCaderno | null }> {
  return post<{ caderno: TopicQuizCaderno | null }>({ action: "save", ...quiz });
}
